const SHOPIFY_LOGIN_FORM_ELEMENT = 'shopify-login-form';
const SHOP_LOGIN_ELEMENT = 'shop-login';
const CUSTOMER_ACCOUNT_FALLBACK_DELAY = 1_600;
const ACCOUNT_AUTH_BRANDING_MAX_FRAMES = 90;
const ACCOUNT_EMAIL_TRIGGER_TEXT = 'More ways to sign in';
const ACCOUNT_EMAIL_ACTION_LABEL = 'SIGN IN WITH EMAIL';
const ACCOUNT_AUTH_HEADING_TEXT = 'Sign in or create account';
const ACCOUNT_AUTH_HEADING_LABEL = 'SIGN IN - OR - CREATE ACCOUNT';
const ACCOUNT_AUTH_POINTER_FOCUS_ATTRIBUTE = 'data-nw-account-pointer-focus';
const ACCOUNT_AUTH_HEADING_ATTRIBUTE = 'data-nw-account-heading';
/* Shopify's public account tokens stop at these nested login controls. Keep this
   presentation-only enhancement bounded to open roots so authentication remains
   Shopify-owned and a future closed or changed root falls back to native UI. */
const ACCOUNT_AUTH_DIALOG_STYLE = `
  .account__heading[${ACCOUNT_AUTH_HEADING_ATTRIBUTE}] {
    font-family: "Akkurat-Mono-Account-Compact", "Akkurat-Mono", "Akkurat-Mono-fallback", monospace;
  }

  button.account__close[${ACCOUNT_AUTH_POINTER_FOCUS_ATTRIBUTE}]:focus {
    outline: none !important;
    box-shadow: none !important;
  }
`;
const ACCOUNT_AUTH_EMAIL_STYLE = `
  button[data-nw-account-email-action] {
    box-sizing: border-box;
    display: flex;
    width: 100%;
    min-height: 48px;
    align-items: center;
    justify-content: center;
    padding: 0 24px;
    border: 1px solid #111;
    border-radius: 0;
    background: #fff;
    color: #111;
    font-family: "Akkurat-Mono", "Akkurat-Mono-fallback", monospace;
    font-size: 14px;
    font-weight: 400;
    letter-spacing: 0.02em;
    line-height: 1.2;
    text-transform: uppercase;
  }

  button[data-nw-account-email-action]:hover,
  button[data-nw-account-email-action]:active {
    background: #111;
    color: #fff;
  }

  button[data-nw-account-email-action]:focus-visible {
    outline: 2px solid #111;
    outline-offset: 3px;
  }
`;
const ACCOUNT_AUTH_SHOP_STYLE = `
  button[aria-label$="with Shop"] {
    background-color: #111 !important;
  }

  button[aria-label$="with Shop"]:enabled:hover,
  button[aria-label$="with Shop"]:enabled:active {
    background-color: #2d2d2d !important;
  }

  button[aria-label$="with Shop"]:focus,
  button[aria-label$="with Shop"]:focus-visible {
    --tw-ring-color: #111 !important;
  }

  button[aria-label$="with Shop"] ~ [data-visible] {
    background-color: #111 !important;
  }
`;

class CustomerAccountDrawer extends HTMLElement {
  constructor() {
    super();
    this.trigger = null;
    this.dialog = null;
    this.closeButton = null;
    this.isBound = false;
    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onTriggerKeydown = this.onTriggerKeydown.bind(this);
    this.onCloseClick = this.onCloseClick.bind(this);
    this.onDialogClick = this.onDialogClick.bind(this);
    this.onDialogClose = this.onDialogClose.bind(this);
  }

  connectedCallback() {
    if (this.isBound) return;
    this.trigger = this.querySelector('[data-account-drawer-trigger]');
    this.dialog = this.querySelector('[data-account-drawer-dialog]');
    this.closeButton = this.querySelector('[data-account-drawer-close]');
    if (!this.trigger || !this.dialog || typeof this.dialog.showModal !== 'function') return;
    this.trigger.addEventListener('click', this.onTriggerClick);
    this.trigger.addEventListener('keydown', this.onTriggerKeydown);
    this.closeButton?.addEventListener('click', this.onCloseClick);
    this.dialog.addEventListener('click', this.onDialogClick);
    this.dialog.addEventListener('close', this.onDialogClose);
    this.isBound = true;
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener('click', this.onTriggerClick);
    this.trigger?.removeEventListener('keydown', this.onTriggerKeydown);
    this.closeButton?.removeEventListener('click', this.onCloseClick);
    this.dialog?.removeEventListener('click', this.onDialogClick);
    this.dialog?.removeEventListener('close', this.onDialogClose);
    if (this.dialog?.open) this.dialog.close();
    this.cleanupOpenState();
    this.isBound = false;
    this.trigger = null;
    this.dialog = null;
    this.closeButton = null;
  }

  onTriggerClick(event) {
    if (
      event.defaultPrevented
      || event.button !== 0
      || event.metaKey
      || event.ctrlKey
      || event.shiftKey
      || event.altKey
    ) return;
    if (this.dialog.open || !this.openDialog()) return;
    event.preventDefault();
  }

  onTriggerKeydown(event) {
    if (
      event.key !== ' '
      || event.metaKey
      || event.ctrlKey
      || event.shiftKey
      || event.altKey
    ) return;
    event.preventDefault();
    if (this.dialog.open || this.openDialog()) return;
    window.location.assign(this.trigger.href);
  }

  openDialog() {
    this.dispatchEvent(new CustomEvent('nw:account-drawer:opening', { bubbles: true }));
    try {
      this.dialog.showModal();
    } catch {
      return false;
    }
    this.trigger.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('customer-account-drawer-open');
    this.closeButton?.focus({ preventScroll: true });
    return true;
  }

  onCloseClick() {
    this.dialog.close();
  }

  onDialogClick(event) {
    if (event.target === this.dialog) this.dialog.close();
  }

  onDialogClose() {
    this.cleanupOpenState();
    if (this.isConnected) this.trigger.focus({ preventScroll: true });
  }

  cleanupOpenState() {
    document.documentElement.classList.remove('customer-account-drawer-open');
    this.trigger?.setAttribute('aria-expanded', 'false');
  }
}

class CustomerAccountAuthLauncher extends HTMLElement {
  constructor() {
    super();
    this.component = null;
    this.fallbackLink = null;
    this.fallbackTimer = null;
    this.isBound = false;
    this.isOpen = false;
    this.handoffRequested = false;
    this.pendingNativeOpen = null;
    this.pendingNativeOpenToken = 0;
    this.authBrandingFrame = null;
    this.authBrandingFrameCount = 0;
    this.openedFromPointer = false;
    this.onComponentActivation = this.onComponentActivation.bind(this);
    this.onComponentKeydown = this.onComponentKeydown.bind(this);
    this.onComponentOpen = this.onComponentOpen.bind(this);
    this.onComponentClose = this.onComponentClose.bind(this);
  }

  connectedCallback() {
    if (this.isBound) return;
    this.component = this.querySelector('[data-account-auth-component]');
    this.fallbackLink = this.querySelector('[data-account-auth-fallback]');
    if (!this.component || !this.fallbackLink) return;
    this.component.addEventListener('click', this.onComponentActivation, true);
    this.component.addEventListener('keydown', this.onComponentKeydown, true);
    this.component.addEventListener('open', this.onComponentOpen);
    this.component.addEventListener('close', this.onComponentClose);
    this.isBound = true;
  }

  disconnectedCallback() {
    this.component?.removeEventListener('click', this.onComponentActivation, true);
    this.component?.removeEventListener('keydown', this.onComponentKeydown, true);
    this.component?.removeEventListener('open', this.onComponentOpen);
    this.component?.removeEventListener('close', this.onComponentClose);
    this.clearFallbackTimer();
    this.stopNativeAuthBranding();
    this.clearNativePointerFocus();
    this.pendingNativeOpenToken += 1;
    this.pendingNativeOpen = null;
    this.isBound = false;
    this.isOpen = false;
    this.handoffRequested = false;
    this.openedFromPointer = false;
    this.component = null;
    this.fallbackLink = null;
  }

  onComponentActivation(event) {
    if (
      this.isOpen
      || event.defaultPrevented
      || (event.button !== undefined && event.button !== 0)
      || event.metaKey
      || event.ctrlKey
      || event.shiftKey
      || event.altKey
    ) return;
    this.openedFromPointer = event.detail !== 0;
    if (this.shouldWaitForNativeLogin()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.requestOverlayHandoff();
      this.openNativeWhenReady();
      return;
    }
    this.applyNativeAuthBranding();
    this.requestOverlayHandoff();
    this.startFallbackTimer();
  }

  onComponentKeydown() {
    this.openedFromPointer = false;
    this.clearNativePointerFocus();
  }

  onComponentOpen() {
    this.isOpen = true;
    this.requestOverlayHandoff();
    this.clearFallbackTimer();
    this.startNativeAuthBranding();
  }

  onComponentClose() {
    this.isOpen = false;
    this.handoffRequested = false;
    this.openedFromPointer = false;
    this.clearFallbackTimer();
    this.stopNativeAuthBranding();
    this.clearNativePointerFocus();
  }

  requestOverlayHandoff() {
    if (this.handoffRequested) return;
    this.handoffRequested = true;
    this.dispatchEvent(new CustomEvent('nw:account-drawer:opening', { bubbles: true }));
  }

  shouldWaitForNativeLogin() {
    // Shopify snapshots the mobile dialog height while its lazy loader is present.
    // Let the real form replace that loader before the first height measurement.
    return (
      typeof customElements.get === 'function'
      && typeof customElements.whenDefined === 'function'
      && !customElements.get(SHOPIFY_LOGIN_FORM_ELEMENT)
      && typeof this.component?.showModal === 'function'
    );
  }

  openNativeWhenReady() {
    if (this.pendingNativeOpen) return;
    const component = this.component;
    const requestToken = this.pendingNativeOpenToken + 1;
    this.pendingNativeOpenToken = requestToken;
    this.startFallbackTimer();
    this.pendingNativeOpen = customElements.whenDefined(SHOPIFY_LOGIN_FORM_ELEMENT)
      .then(() => this.waitForNativeLoginRender())
      .then(() => {
        if (
          !this.isConnected
          || this.isOpen
          || this.component !== component
          || this.pendingNativeOpenToken !== requestToken
        ) return;
        this.applyNativeAuthBranding();
        this.startFallbackTimer();
        try {
          const opening = component.showModal();
          if (opening && typeof opening.catch === 'function') {
            opening.catch(() => this.navigateToFallback(requestToken));
          }
        } catch {
          this.navigateToFallback(requestToken);
        }
      })
      .catch(() => this.navigateToFallback(requestToken))
      .finally(() => {
        if (this.pendingNativeOpenToken === requestToken) this.pendingNativeOpen = null;
      });
  }

  waitForNativeLoginRender() {
    if (typeof window.requestAnimationFrame !== 'function') return Promise.resolve();
    return new Promise((resolve) => {
      window.requestAnimationFrame(() => window.requestAnimationFrame(resolve));
    });
  }

  startNativeAuthBranding() {
    this.stopNativeAuthBranding();
    this.authBrandingFrameCount = 0;

    const applyBranding = () => {
      this.authBrandingFrame = null;
      if (!this.isConnected || !this.isOpen) return;

      const state = this.applyNativeAuthBranding();
      if (
        state.headingStyled
        && state.closeFocusStyled
        && state.emailActionStyled
        && state.shopActionStyled
      ) return;
      if (this.authBrandingFrameCount >= ACCOUNT_AUTH_BRANDING_MAX_FRAMES) return;
      if (typeof window.requestAnimationFrame !== 'function') return;

      this.authBrandingFrameCount += 1;
      this.authBrandingFrame = window.requestAnimationFrame(applyBranding);
    };

    applyBranding();
  }

  stopNativeAuthBranding() {
    if (
      this.authBrandingFrame !== null
      && typeof window.cancelAnimationFrame === 'function'
    ) {
      window.cancelAnimationFrame(this.authBrandingFrame);
    }
    this.authBrandingFrame = null;
    this.authBrandingFrameCount = 0;
  }

  applyNativeAuthBranding() {
    const componentRoot = this.component?.shadowRoot;
    const heading = componentRoot?.querySelector?.('#dialog-heading.account__heading');
    const normalizedHeading = this.normalizeAuthCopy(heading?.textContent);
    if (heading && normalizedHeading === ACCOUNT_AUTH_HEADING_TEXT) {
      heading.textContent = ACCOUNT_AUTH_HEADING_LABEL;
      heading.setAttribute(ACCOUNT_AUTH_HEADING_ATTRIBUTE, '');
    } else if (heading && normalizedHeading === ACCOUNT_AUTH_HEADING_LABEL) {
      heading.setAttribute(ACCOUNT_AUTH_HEADING_ATTRIBUTE, '');
    } else if (heading) {
      heading.removeAttribute(ACCOUNT_AUTH_HEADING_ATTRIBUTE);
    }

    const closeButton = componentRoot?.querySelector?.('#close-button.account__close');
    if (closeButton) {
      if (this.openedFromPointer) {
        closeButton.setAttribute(ACCOUNT_AUTH_POINTER_FOCUS_ATTRIBUTE, '');
      } else {
        closeButton.removeAttribute(ACCOUNT_AUTH_POINTER_FOCUS_ATTRIBUTE);
      }
    }

    if (heading || closeButton) {
      this.ensureShadowStyle(
        componentRoot,
        'data-nw-account-auth-dialog-branding',
        ACCOUNT_AUTH_DIALOG_STYLE,
      );
    }

    const loginForm = this.findOpenShadowElement(
      componentRoot,
      SHOPIFY_LOGIN_FORM_ELEMENT,
    );
    const loginFormRoot = loginForm?.shadowRoot;
    const emailButton = loginFormRoot
      ? Array.from(loginFormRoot.querySelectorAll?.('button') || [])
        .find((button) => (
          button.hasAttribute?.('data-nw-account-email-action')
          || this.normalizeAuthCopy(button.textContent) === ACCOUNT_EMAIL_TRIGGER_TEXT
        ))
      : null;
    if (emailButton) {
      emailButton.setAttribute('data-nw-account-email-action', '');
      if (
        this.normalizeAuthCopy(emailButton.textContent) === ACCOUNT_EMAIL_TRIGGER_TEXT
        && (emailButton.children?.length || 0) === 0
      ) {
        emailButton.textContent = ACCOUNT_EMAIL_ACTION_LABEL;
      }
      this.ensureShadowStyle(
        loginFormRoot,
        'data-nw-account-auth-email-branding',
        ACCOUNT_AUTH_EMAIL_STYLE,
      );
    }

    const shopLogin = this.findOpenShadowElement(componentRoot, SHOP_LOGIN_ELEMENT);
    const shopLoginRoot = shopLogin?.shadowRoot;
    const shopButton = shopLoginRoot?.querySelector?.('button[aria-label$="with Shop"]');
    if (shopButton) {
      this.ensureShadowStyle(
        shopLoginRoot,
        'data-nw-account-auth-shop-branding',
        ACCOUNT_AUTH_SHOP_STYLE,
      );
    }

    return {
      headingStyled: Boolean(
        heading?.hasAttribute?.(ACCOUNT_AUTH_HEADING_ATTRIBUTE)
      ),
      closeFocusStyled: Boolean(closeButton),
      emailActionStyled: Boolean(emailButton),
      shopActionStyled: Boolean(shopButton),
    };
  }

  clearNativePointerFocus() {
    this.component?.shadowRoot
      ?.querySelector?.('#close-button.account__close')
      ?.removeAttribute?.(ACCOUNT_AUTH_POINTER_FOCUS_ATTRIBUTE);
  }

  findOpenShadowElement(root, selector) {
    if (!root || typeof root.querySelector !== 'function') return null;

    const directMatch = root.querySelector(selector);
    if (directMatch) return directMatch;

    for (const element of root.querySelectorAll?.('*') || []) {
      if (!element.shadowRoot) continue;
      const nestedMatch = this.findOpenShadowElement(element.shadowRoot, selector);
      if (nestedMatch) return nestedMatch;
    }
    return null;
  }

  ensureShadowStyle(root, marker, cssText) {
    if (
      !root
      || typeof root.querySelector !== 'function'
      || typeof root.appendChild !== 'function'
      || typeof document.createElement !== 'function'
    ) return null;

    const existingStyle = root.querySelector(`style[${marker}]`);
    if (existingStyle) return existingStyle;

    const style = document.createElement('style');
    style.setAttribute(marker, '');
    style.textContent = cssText;
    root.appendChild(style);
    return style;
  }

  normalizeAuthCopy(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  startFallbackTimer() {
    this.clearFallbackTimer();
    const requestToken = this.pendingNativeOpenToken;
    this.fallbackTimer = window.setTimeout(() => {
      this.fallbackTimer = null;
      this.navigateToFallback(requestToken);
    }, CUSTOMER_ACCOUNT_FALLBACK_DELAY);
  }

  navigateToFallback(requestToken) {
    if (
      !this.isConnected
      || this.isOpen
      || !this.fallbackLink
      || requestToken !== this.pendingNativeOpenToken
    ) return;
    this.pendingNativeOpenToken += 1;
    this.pendingNativeOpen = null;
    this.handoffRequested = false;
    this.openedFromPointer = false;
    this.clearFallbackTimer();
    this.clearNativePointerFocus();
    window.location.assign(this.fallbackLink.href);
  }

  clearFallbackTimer() {
    if (this.fallbackTimer === null) return;
    window.clearTimeout(this.fallbackTimer);
    this.fallbackTimer = null;
  }
}

if (!customElements.get('account-drawer')) {
  customElements.define('account-drawer', CustomerAccountDrawer);
}

if (!customElements.get('account-auth-launcher')) {
  customElements.define('account-auth-launcher', CustomerAccountAuthLauncher);
}
